import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { blogPosts, getBlogPost, type BlogPost } from "@/data/blog";
import { publicPageHeaders } from "@/lib/http-cache";

export const Route = createFileRoute("/blog/$slug")({
  headers: publicPageHeaders,
  loader: ({ params }) => {
    const post = getBlogPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    const url = `https://carentals.ro/blog/${params.slug}`;
    if (!post) return {};
    return {
      meta: [
        { title: post.metaTitle },
        { name: "description", content: post.description },
        { name: "keywords", content: post.keywords.join(", ") },
        { property: "og:title", content: post.metaTitle },
        { property: "og:description", content: post.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            datePublished: post.datePublished,
            dateModified: post.datePublished,
            mainEntityOfPage: url,
            author: { "@type": "Organization", name: "Car Rental Bucharest" },
            publisher: { "@id": "https://carentals.ro/#organization" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://carentals.ro/" },
              { "@type": "ListItem", position: 2, name: "Guides", item: "https://carentals.ro/blog" },
              { "@type": "ListItem", position: 3, name: post.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <div className="container-page py-24 text-center">
      <h1 className="text-3xl font-bold">Guide not found</h1>
      <Button asChild variant="gold" className="mt-6">
        <Link to="/blog">Back to all guides</Link>
      </Button>
    </div>
  ),
});

function BlogPostPage() {
  const { post } = Route.useLoaderData() as { post: BlogPost };
  const others = blogPosts.filter((p) => p.slug !== post.slug);

  return (
    <article className="container-page py-16">
      <nav className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        /{" "}
        <Link to="/blog" className="hover:text-foreground">
          Guides
        </Link>
      </nav>

      <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-tight sm:text-5xl">
        {post.title}
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {new Date(post.datePublished).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}{" "}
        · {post.readingMinutes} min read
      </p>

      <p className="mt-8 max-w-3xl text-lg leading-relaxed text-foreground/90">{post.intro}</p>

      <div className="mt-12 max-w-3xl space-y-10">
        {post.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-display text-2xl font-bold">{section.heading}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph} className="mt-4 leading-relaxed text-muted-foreground">
                {paragraph}
              </p>
            ))}
            {section.bullets && (
              <ul className="mt-5 space-y-2.5">
                {section.bullets.map((bullet) => (
                  <li key={bullet} className="flex gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-gold" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <div className="mt-14 max-w-3xl rounded-xl border border-gold/30 bg-card p-8 shadow-card">
        <p className="eyebrow">Book with zero deposit</p>
        <h2 className="mt-3 font-display text-2xl font-bold">
          No deposit, no credit card, insurance included
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          28 cars available in Bucharest from €44/day, delivered free in the city and at Otopeni
          Airport.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild variant="gold">
            <Link to="/rentals">See the fleet</Link>
          </Button>
          <Button asChild variant="outline">
            <a href="https://wa.me/40738792499" target="_blank" rel="noreferrer">WhatsApp us</a>
          </Button>
        </div>
      </div>

      <div className="mt-16 max-w-3xl">
        <h2 className="font-display text-xl font-bold">More guides</h2>
        <ul className="mt-4 space-y-3">
          {others.map((other) => (
            <li key={other.slug}>
              <Link
                to="/blog/$slug"
                params={{ slug: other.slug }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gold"
              >
                {other.title} <ArrowRight className="size-4" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}
